<template>
  <div class="ai-reg">
    <project-form
      v-if="editing"
      :key="editing.project ? editing.project.id : 'new'"
      :project="editing.project"
      @saved="onSaved"
      @deleted="onDeleted"
      @cancel="editing = null"
    />
    <registry-import
      v-else-if="importing"
      @done="load"
      @cancel="importing = false"
    />
    <template v-else>
      <div class="ai-reg__toolbar">
        <input
          v-model="q"
          type="search"
          class="ai-input ai-reg__search"
          maxlength="100"
          placeholder="Шифр, название, адрес, заказчик или ответственный"
          aria-label="Поиск проектов"
          @input="search"
        >
        <select
          v-model="status"
          class="ai-input ai-reg__status"
          aria-label="Статус проектов"
          @change="load"
        >
          <option value="">
            Все проекты
          </option>
          <option value="active">
            Действующие
          </option>
          <option value="closed">
            Закрытые
          </option>
        </select>
      </div>
      <div class="ai-reg__toolbar">
        <button
          type="button"
          class="ai-button is-primary"
          :disabled="!canEdit"
          @click="editing = { project: null }"
        >
          <ai-icon
            name="plus"
            :size="14"
          />
          Добавить проект
        </button>
        <button
          type="button"
          class="ai-button"
          :disabled="!canEdit"
          @click="importing = true"
        >
          <ai-icon
            name="upload"
            :size="14"
          />
          Импорт
        </button>
        <span class="ai-reg__export">
          <button
            type="button"
            class="ai-button"
            :disabled="!!exporting || !total"
            title="Скачать реестр в Excel"
            @click="exportAs('xlsx')"
          >
            <span
              v-if="exporting === 'xlsx'"
              class="ai-spinner ai-spinner--small"
            />
            <ai-icon
              v-else
              name="download"
              :size="14"
            />
            Excel
          </button>
          <button
            type="button"
            class="ai-button"
            :disabled="!!exporting || !total"
            title="Скачать реестр в CSV («;», UTF-8)"
            @click="exportAs('csv')"
          >
            <span
              v-if="exporting === 'csv'"
              class="ai-spinner ai-spinner--small"
            />
            CSV
          </button>
        </span>
      </div>

      <p
        v-if="error"
        class="ai-reg__line is-error"
        role="alert"
      >
        {{ error }}
      </p>
      <p
        v-else-if="loading && !items.length"
        class="ai-reg__line"
      >
        <span class="ai-spinner ai-spinner--small" />
        Загружаю реестр…
      </p>
      <p
        v-else
        class="ai-reg__line"
      >
        {{ totals }}
      </p>
      <p
        v-if="notice"
        class="ai-reg__line is-success"
        role="status"
      >
        {{ notice }}
      </p>

      <div
        v-if="items.length"
        class="ai-reg__table"
      >
        <table>
          <thead>
            <tr>
              <th>Шифр</th>
              <th>Название</th>
              <th>Заказчик</th>
              <th>Статус</th>
              <th>Срок</th>
              <th>Ответственные</th>
              <th class="ai-reg__actions-col">
                <span class="ai-reg__hidden">Действия</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template v-for="project in items">
              <tr
                :key="project.id"
                @dblclick="edit(project)"
              >
                <!-- The code opens the project: on a phone the row actions are off screen. -->
                <td class="ai-reg__code">
                  <button
                    v-if="canEdit"
                    type="button"
                    class="ai-link-button"
                    :title="`Открыть проект ${project.code}`"
                    @click="edit(project)"
                  >
                    {{ project.code }}
                  </button>
                  <template v-else>
                    {{ project.code }}
                  </template>
                </td>
                <td class="ai-reg__name">
                  <span>{{ project.name || '—' }}</span>
                  <span
                    v-if="project.address"
                    class="ai-reg__muted"
                  >{{ project.address }}</span>
                </td>
                <td>{{ project.customer || '—' }}</td>
                <td>
                  <span
                    class="ai-reg__status-chip"
                    :class="project.status === 'closed' ? 'is-closed' : 'ai-tone is-success'"
                  >
                    {{ project.status === 'closed' ? 'закрыт' : 'действует' }}
                  </span>
                </td>
                <td class="ai-reg__term">
                  {{ term(project) }}
                </td>
                <td :title="(project.people || []).join('\n')">
                  {{ people(project) }}
                </td>
                <td class="ai-reg__actions-col">
                  <button
                    type="button"
                    class="ai-icon-button is-small"
                    :title="`Изменить ${project.code}`"
                    :disabled="!canEdit"
                    @click="edit(project)"
                  >
                    <ai-icon
                      name="edit"
                      :size="14"
                    />
                  </button>
                  <button
                    type="button"
                    class="ai-icon-button is-small"
                    :title="`Удалить ${project.code}`"
                    :disabled="!canEdit || removing === project.id"
                    @click="deleting = project.id"
                  >
                    <ai-icon
                      name="trash"
                      :size="14"
                    />
                  </button>
                </td>
              </tr>
              <tr
                v-if="deleting === project.id"
                :key="`${project.id}-delete`"
                class="ai-reg__confirm"
              >
                <td colspan="7">
                  <confirm-inline
                    :text="`Удалить проект ${project.code} из реестра?`"
                    :busy="removing === project.id"
                    @confirm="remove(project)"
                    @cancel="deleting = null"
                  />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <p
        v-else-if="!loading && !error && total"
        class="ai-reg__empty"
      >
        Ничего не найдено.
      </p>
      <p
        v-else-if="!loading && !error"
        class="ai-reg__empty"
      >
        Реестр пуст. Добавьте проекты по одному или загрузите таблицу Excel или CSV: без реестра проверка
        не сверяет папки, шифры и ответственных по проектам.
      </p>
    </template>
  </div>
</template>

<script>
  import dayjs from 'dayjs';
  import * as api from '@/api/methods/assistant';
  import AiIcon from './AiIcon.vue';
  import ConfirmInline from './ConfirmInline.vue';
  import ProjectForm from './ProjectForm.vue';
  import RegistryImport from './RegistryImport.vue';
  import { count } from './format';

  const SEARCH_DELAY = 300;
  const PROJECTS = ['проект', 'проекта', 'проектов'];

  const date = (value) => (value ? dayjs(value).format('DD.MM.YYYY') : '');

  // Saves a base64 file of the export through a temporary link.
  function download({ filename, contentType, content }) {
    const bytes = atob(content);
    const buffer = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i += 1) buffer[i] = bytes.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([buffer], { type: contentType || 'application/octet-stream' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'Реестр проектов';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  export default {
    name: 'SideworkRegistry',
    components: { AiIcon, ConfirmInline, ProjectForm, RegistryImport },
    data: () => ({
      items: [],
      total: 0,
      canEdit: false,
      q: '',
      status: '',
      loading: false,
      error: '',
      notice: '',
      editing: null,
      importing: false,
      exporting: '',
      removing: null,
      // The project whose deletion is being confirmed (a row under it).
      deleting: null,
      timer: null,
    }),
    computed: {
      filtered() {
        return !!(this.q.trim() || this.status);
      },
      totals() {
        if (!this.total) return 'В реестре пока нет проектов.';
        const all = `В реестре ${count(this.total, PROJECTS)}`;
        return this.filtered ? `${all}, показано ${this.items.length}.` : `${all}.`;
      },
    },
    created() {
      this.load();
    },
    beforeDestroy() {
      clearTimeout(this.timer);
    },
    methods: {
      term(project) {
        const from = date(project.dateFrom);
        const to = date(project.dateTo);
        if (from && to) return `${from} – ${to}`;
        if (from) return `с ${from}`;
        if (to) return `до ${to}`;
        return '—';
      },
      // Short list of the responsible people: two names and the number of the others.
      people(project) {
        const list = project.people || [];
        if (!list.length) return '—';
        const shown = list.slice(0, 2).join(', ');
        return list.length > 2 ? `${shown} и ещё ${list.length - 2}` : shown;
      },
      search() {
        clearTimeout(this.timer);
        this.timer = setTimeout(this.load, SEARCH_DELAY);
      },
      async load() {
        clearTimeout(this.timer);
        this.loading = true;
        this.error = '';
        const params = {};
        if (this.q.trim()) params.q = this.q.trim();
        if (this.status) params.status = this.status;
        try {
          const { data } = await api.listProjects(params);
          this.items = data.items || [];
          this.total = data.total || 0;
          this.canEdit = data.canEdit !== false;
        } catch (error) {
          this.error = api.errorText(error, 'Не удалось загрузить реестр');
        } finally {
          this.loading = false;
        }
      },
      edit(project) {
        if (!this.canEdit) return;
        this.notice = '';
        this.editing = { project };
      },
      onSaved(project) {
        const isNew = !this.editing.project;
        this.editing = null;
        this.notice = isNew ? `Проект ${project.code} добавлен.` : `Проект ${project.code} сохранён.`;
        this.load();
      },
      onDeleted(project) {
        this.editing = null;
        this.notice = `Проект ${project.code} удалён.`;
        this.load();
      },
      async remove(project) {
        this.removing = project.id;
        this.error = '';
        this.notice = '';
        try {
          await api.deleteProject(project.id);
          this.notice = `Проект ${project.code} удалён.`;
          await this.load();
        } catch (error) {
          this.error = api.errorText(error, 'Не удалось удалить проект');
        } finally {
          this.removing = null;
          this.deleting = null;
        }
      },
      async exportAs(format) {
        this.exporting = format;
        this.error = '';
        try {
          const { data } = await api.exportRegistry(format);
          download(data);
        } catch (error) {
          this.error = api.errorText(error, 'Не удалось выгрузить реестр');
        } finally {
          this.exporting = '';
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .ai-reg {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .ai-reg__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }

  .ai-widget .ai-reg__search {
    flex: 1 1 260px;
    width: auto;
    min-width: 0;
  }

  .ai-widget .ai-reg__status {
    flex: 0 0 auto;
    width: auto;
  }

  .ai-reg__export {
    display: inline-flex;
    gap: 6px;
    margin-left: auto;
  }

  .ai-reg__line {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    color: var(--ui-text-muted);
    font-size: 13px;

    &.is-error {
      color: var(--ui-danger);
    }

    &.is-success {
      color: var(--ui-success);
    }
  }

  .ai-reg__table {
    overflow-x: auto;
    border: 1px solid var(--ui-border);
    border-radius: var(--ui-radius-sm);

    table {
      width: 100%;
      min-width: 640px;
      border-collapse: separate;
      border-spacing: 0;
      font-size: 13px;
    }

    th,
    td {
      padding: 6px 8px;
      border-bottom: 1px solid var(--ui-border);
      color: var(--ui-text);
      text-align: left;
      vertical-align: top;
      // Whole words decide the column widths; only a word longer than the column breaks.
      overflow-wrap: break-word;
    }

    th {
      background: var(--ui-surface-soft);
      color: var(--ui-text-muted);
      font-weight: 500;
      white-space: nowrap;
    }

    tbody tr:last-child td {
      border-bottom: 0;
    }

    tbody tr:hover td {
      background: var(--ui-surface-soft);
    }
  }

  .ai-reg__name {
    min-width: 140px;

    > span {
      display: block;
    }
  }

  .ai-reg__code {
    font-weight: 500;
    white-space: nowrap;

    .ai-link-button {
      font-weight: 500;
    }
  }

  // Dates do not break inside; the period wraps after the dash.
  .ai-reg__term {
    min-width: 84px;
  }

  .ai-reg__muted {
    color: var(--ui-text-muted);
    font-size: 12px;
  }

  .ai-reg__status-chip {
    display: inline-block;
    padding: 0 7px;
    border-radius: 999px;
    font-size: 12px;
    white-space: nowrap;

    &.is-closed {
      background: var(--ui-bg-strong);
      color: var(--ui-text-muted);
    }
  }

  .ai-reg__table .ai-reg__confirm td {
    background: var(--ui-surface-soft);
  }

  .ai-reg__confirm .ai-confirm {
    justify-content: flex-start;
  }

  .ai-reg__table .ai-reg__actions-col {
    width: 60px;
    white-space: nowrap;
  }

  .ai-reg__hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }

  .ai-reg__empty {
    margin: 8px 0;
    color: var(--ui-text-muted);
    font-size: 13px;
    line-height: 1.5;
    text-align: center;
  }
</style>
